import React, { useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import { IVisitRequestForm } from '@interfaces/VisitRequest.interface';
import { IMappedVisitRequestResponse } from '@interfaces/VisitRequestLookups.interface';
import { useTranslate } from '@translations/useTranslate';
import { IFormItemType } from '@interfaces/FormItemType';
import { CustomButton, FormContainer } from '@ui/components/atoms';
import { Form } from 'react-bootstrap';
import ArrowIcon from '@assets/svg/arrow.svg';
import { SubFormSection } from '@ui/components/molecules/SubFormSection';
import { useSubmitVisitRequestForm } from '@services/VisitRequest';

export interface VisitRequestFormProps {
  requestId: string;
  requestData: IMappedVisitRequestResponse;
  onRequestApprove: () => void;
  onRequestSuccess: () => void;
  onRequestFailed: () => void;
}

export const VisitRequestVIPForm: React.FC<VisitRequestFormProps> = ({
  requestId,
  requestData,
  onRequestApprove,
  onRequestSuccess,
  onRequestFailed,
}) => {
  const { t } = useTranslate();
  const [error, setError] = React.useState<string | undefined>();

  const { mutateAsync: submitRequest, isLoading } = useSubmitVisitRequestForm();

  const handleApprove = useCallback(() => {
    onRequestApprove();
  }, [onRequestApprove]);

  const handleReject = useCallback(() => {
    const formData = new FormData();
    formData.append('isRejected', 'true');
    formData.append('requestId', requestId);
    submitRequest(formData, {
      onError: (error) => {
        const errorData = error?.response?.data as { errorMessage?: string };
        setError(errorData?.errorMessage ?? t('global.error'));
        onRequestFailed();
      },
    })
      .then(() => {
        onRequestSuccess();
        //navigate to next page
      })
      .catch(() => {
        //Ignore the exception
      });
  }, [requestId, submitRequest, t, onRequestSuccess, onRequestFailed]);

  const formik = useFormik<IVisitRequestForm>({
    initialValues: {
      requestId: requestId,
      visitorName: '',
      visitorEmail: '',
      visitDates: undefined,
      identity: '',
      phoneNumber: '',
      identityValue: '',
    },
    onSubmit: () => {
      // No-op
    },
  });

  const userDetailsFormItems: IFormItemType[] = useMemo(() => {
    const formItems: IFormItemType[] = [
      {
        id: 'visitorName',
        label: t('visit.request.visitorName.label'),
        placeholder: t('visit.request.visitorName.placeholder'),
        type: 'text',
        required: true,
        value: requestData.visitorName,
        maxLength: 30,
        disabled: true,
        onChange: (value) => {
          formik.setFieldValue('visitorName', value);
        },
      },
      {
        id: 'phoneNumber',
        label: t('visit.request.phoneNumber.label'),
        placeholder: t('visit.request.phoneNumber.placeholder'),
        type: 'text',
        required: true,
        value: requestData.mobileNumber,
        disabled: true,
        onChange: (value) => {
          formik.setFieldValue('phoneNumber', value);
        },
      },
      {
        id: 'visitorEmail',
        label: t('visit.request.email.label'),
        placeholder: t('visit.request.email.placeholder'),
        type: 'text',
        required: true,
        value: requestData.visitorEmail,
        disabled: true,
        onChange: (value) => {
          formik.setFieldValue('visitorEmail', value);
        },
      },
      {
        id: 'visitDates',
        label: 'Visit Date and Time',
        placeholder: '',
        type: 'datetime-local',
        required: true,
        value: requestData.visitDateTime,
        disabled: true,
        onChange: (value) => {
          formik.setFieldValue('visitDates', value);
        },
      },
      {
        id: 'identity',
        label: t('visit.request.identity.label'),
        placeholder: t('visit.request.identity.placeholder'),
        type: 'select',
        required: true,
        disabled: true,
        options: requestData?.identityList,
        value: requestData?.identity,
        onChange: (value) => {
          formik.setFieldValue('identity', value);
        },
      },
      {
        id: 'identityValue',
        label: t('visit.request.DocumentID.label'),
        placeholder: t('visit.request.DocumentID.placeholder'),
        type: 'text',
        required: true,
        value: requestData.identityValue,
        disabled: true,
        onChange: (value) => {
          formik.setFieldValue('identityValue', value);
        },
      },
    ];

    return formItems;
  }, [formik, t, requestData]);

  return (
    <FormContainer error={error}>
      <SubFormSection items={userDetailsFormItems} />
      <Form.Group className="w-100 pb-3 px-3 d-flex flex-column justify-content-start align-items-start" />
      <div className="w-100 pt-3 pb-5 d-flex justify-content-center align-items-center">
        <CustomButton
          variant="primary"
          text={'Approve'}
          suffixIcon={ArrowIcon}
          onClick={handleApprove}
          disabled={isLoading}
        />
        <CustomButton
          variant="outline-dark"
          text={'Reject'}
          onClick={handleReject}
          disabled={isLoading}
          className="mx-3"
        />
      </div>
    </FormContainer>
  );
};
